import { PushNotifications, PushNotificationSchema, Token } from '@capacitor/push-notifications'
import { Capacitor } from '@capacitor/core'
import { initializeApp } from 'firebase/app'
import { getMessaging, getToken as getTokenForWeb, isSupported, onMessage } from 'firebase/messaging'

const firebaseConfig = {
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
    databaseURL: process.env.REACT_APP_FIREBASE_DATABASE_URL,
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
    storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
    appId: process.env.REACT_APP_FIREBASE_APP_ID,
}

const VAPID_KEY = process.env.REACT_APP_FIREBASE_VAMPID  // <-- fixed typo
const app = initializeApp(firebaseConfig)

// ---------- Web (browser/PWA) helpers ----------
const getFirebaseMessaging = async () => {
  const supported = await isSupported().catch(() => false)
  if (!supported) {
    console.log('Firebase messaging not supported in this browser')
    return null
  }
  return getMessaging(app)
}

// ---------- Tokens ----------
export const requestToken = async (): Promise<string> => {
  if (Capacitor.isNativePlatform()) {
    const perms = await PushNotifications.checkPermissions()
    if (perms.receive !== 'granted') {
      const req = await PushNotifications.requestPermissions()
      if (req.receive !== 'granted') return ''
    }
    return getTokenForAndroidOnce()
  }

  // Web / PWA
  const messaging = await getFirebaseMessaging()
  if (!messaging) return ''

  if (!VAPID_KEY) {
    console.warn('Missing VAPID key for web push')
    return ''
  }

  const swReg =
    (await navigator.serviceWorker.getRegistration('/firebase-messaging-sw.js')) ??
    (await navigator.serviceWorker.register('/firebase-messaging-sw.js', { updateViaCache: 'none' }))

  return getTokenForWeb(messaging, { vapidKey: VAPID_KEY, serviceWorkerRegistration: swReg })
}

const getTokenForAndroidOnce = (): Promise<string> =>
  new Promise((resolve, reject) => {
    let regHandle: any
    let errHandle: any

    const cleanup = async () => {
      try {
        if (regHandle) await regHandle.remove()
        if (errHandle) await errHandle.remove()
      } catch {}
    }

    const onOk = (t: Token) => {
      cleanup()
      resolve(t.value)
    }
    const onErr = (e: any) => {
      cleanup()
      reject(e)
    }

    // Add listeners first to avoid races
    Promise.all([
      PushNotifications.addListener('registration', onOk),
      PushNotifications.addListener('registrationError', onErr),
    ]).then(([r, e]) => {
      regHandle = r
      errHandle = e
      PushNotifications.register()
    })
  })

// ---------- Foreground message listener ----------
type Callback = (msg: { id: string; text: string; data?: Record<string, string> }) => void

export const onMessageListener = async (
  callback: Callback,
  clearRequestInterval: () => void,
  requestSettings: () => void
): Promise<() => void> => {

  let timer: ReturnType<typeof setTimeout> | null = null
  const scheduleRefresh = () => {
    clearRequestInterval()
    if (timer) clearTimeout(timer)
    timer = setTimeout(requestSettings, 15000)
  }

  if (Capacitor.isNativePlatform()) {
    const handleCustomMessage = (event: any) => {
      try {
        const raw = event?.detail ?? '{}'
        const data = typeof raw === 'string' ? JSON.parse(raw) : raw
        console.log('[Push] received (custom service)', data)
        scheduleRefresh()
        // MainActivity nests the full FCM data envelope under `data`.
        const envelope = (data.data ?? {}) as Record<string, string>
        callback({
          id: envelope.hash ?? data.id ?? String(Date.now()),
          text: data.message ?? data.body ?? '',
          data: envelope,
        })
      } catch (e) {
        console.error('Failed to parse foreground payload', e, event?.detail)
      }
    }

    window.addEventListener('firebase-data-message', handleCustomMessage)

    // Optional: backup listener for notification-type payloads
    const receivedHandle = await PushNotifications.addListener(
      'pushNotificationReceived',
      (n: PushNotificationSchema) => {
        // If your server ever sends "notification" payloads, this will fire in foreground
        console.log('[Push] received (Capacitor)', n)
        scheduleRefresh()
        const envelope = (n.data ?? {}) as Record<string, string>
        callback({
          id: envelope.hash ?? n.id ?? String(Date.now()),
          text: n.body ?? '',
          data: envelope,
        })
      }
    )

    return () => {
      if (timer) clearTimeout(timer)
      window.removeEventListener('firebase-data-message', handleCustomMessage)
      receivedHandle?.remove?.()
    }
  }

  // Web
  const messaging = await getFirebaseMessaging()
  if (!messaging) return () => {}

  const unsub = onMessage(messaging, (payload) => {
    console.log('[Push] received (web)', payload)
    scheduleRefresh()
    const envelope = (payload.data ?? {}) as Record<string, string>
    callback({
      id: envelope.hash ?? payload.messageId ?? String(Date.now()),
      text: envelope.message ?? payload.notification?.body ?? '',
      data: envelope,
    })
  })

  return () => {
    if (timer) clearTimeout(timer)
    unsub()
  }
}
